import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Pencil, KeyRound, Trash2 } from "lucide-react";

const ROLES = [
  { value: "super_admin", label_fr: "Super Admin", label_en: "Super Admin",
    desc_fr: "Accès complet · gestion équipe · suppression", desc_en: "Full access · staff management · deletion", color: "bg-amber-500/10 text-amber-700" },
  { value: "admissions_admin", label_fr: "Admissions", label_en: "Admissions",
    desc_fr: "Candidatures · entretiens · acceptations", desc_en: "Applications · interviews · admissions", color: "bg-sky-500/10 text-sky-700" },
  { value: "academic_admin", label_fr: "Académique", label_en: "Academic",
    desc_fr: "Programmes · SIS · bulletins", desc_en: "Programs · SIS · report cards", color: "bg-emerald-500/10 text-emerald-700" },
  { value: "finance_admin", label_fr: "Finance", label_en: "Finance",
    desc_fr: "Paiements · facturation · audit", desc_en: "Payments · billing · audit", color: "bg-rose-500/10 text-rose-700" },
  { value: "teacher", label_fr: "Professeur", label_en: "Teacher",
    desc_fr: "Cours · notes · messagerie (pas d'accès aux paiements)", desc_en: "Courses · grades · messaging (no payment access)", color: "bg-indigo-500/10 text-indigo-700" },
];

const roleMeta = (r) => ROLES.find(x => x.value === r) || { label_fr: r, label_en: r, color: "bg-secondary text-foreground" };

const emptyForm = { email: "", first_name: "", last_name: "", role: "teacher", password: "" };

export default function AdminStaff() {
  const { lang } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pwOpen, setPwOpen] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/staff")
      .then(r => setUsers(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, first_name: u.first_name || "", last_name: u.last_name || "", role: u.role, password: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { email: form.email, first_name: form.first_name, last_name: form.last_name, role: form.role };
        await api.patch(`/admin/staff/${editing.id}`, payload);
        toast.success(lang === "fr" ? "Compte mis à jour" : "Account updated");
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error(lang === "fr" ? "Mot de passe ≥ 8 caractères requis" : "Password ≥ 8 chars required"); return;
        }
        await api.post("/admin/staff", form);
        toast.success(lang === "fr" ? "Compte créé" : "Account created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 8) {
      toast.error(lang === "fr" ? "Mot de passe ≥ 8 caractères" : "Password ≥ 8 chars"); return;
    }
    try {
      await api.post(`/admin/staff/${pwOpen.id}/reset-password`, { new_password: newPw });
      toast.success(lang === "fr" ? "Mot de passe réinitialisé" : "Password reset");
      setPwOpen(null); setNewPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(lang === "fr"
      ? `Supprimer définitivement le compte ${u.email} ?`
      : `Permanently delete account ${u.email}?`)) return;
    try {
      await api.delete(`/admin/staff/${u.id}`);
      toast.success(lang === "fr" ? "Compte supprimé" : "Account deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-staff">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="overline mb-1">OFA France · Admin</p>
          <h1 className="font-serif text-3xl tracking-tight">
            {lang === "fr" ? "Gestion des équipes" : "Staff management"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {lang === "fr"
              ? "Créez et gérez les comptes administrateurs et professeurs. Les rôles déterminent les droits d'accès aux différentes sections (paiements, audit, etc.)."
              : "Create and manage admin and teacher accounts. Roles determine access to different sections (payments, audit, etc.)."}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-staff-btn">
          <UserPlus className="w-4 h-4 mr-1.5" />
          {lang === "fr" ? "Nouveau membre" : "New member"}
        </Button>
      </div>

      {/* Role legend */}
      <Card className="mb-5">
        <CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {lang === "fr" ? "Rôles disponibles" : "Available roles"}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {ROLES.map(r => (
              <div key={r.value} className="rounded border border-border p-2">
                <Badge className={`${r.color} text-[10px] mb-1`}>{lang === "fr" ? r.label_fr : r.label_en}</Badge>
                <div className="text-[11px] text-muted-foreground leading-snug">
                  {lang === "fr" ? r.desc_fr : r.desc_en}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">{lang === "fr" ? "Nom" : "Name"}</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Rôle" : "Role"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Créé" : "Created"}</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const meta = roleMeta(u.role);
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-t border-border hover:bg-secondary/20" data-testid={`staff-row-${u.id}`}>
                      <td className="p-3 font-medium">
                        {u.first_name} {u.last_name}
                        {isMe && <Badge variant="outline" className="ml-2 text-[10px]">{lang === "fr" ? "vous" : "you"}</Badge>}
                      </td>
                      <td className="p-3 text-xs">{u.email}</td>
                      <td className="p-3"><Badge className={`${meta.color} text-[10px]`}>{lang === "fr" ? meta.label_fr : meta.label_en}</Badge></td>
                      <td className="p-3 text-[11px] text-muted-foreground whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US") : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-staff-${u.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => { setPwOpen(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}>
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {!isMe && (
                            <Button size="icon" variant="ghost" onClick={() => remove(u)} data-testid={`delete-staff-${u.id}`}
                                    className="text-destructive hover:text-destructive">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? (lang === "fr" ? "Modifier le membre" : "Edit member")
                       : (lang === "fr" ? "Nouveau membre" : "New member")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{lang === "fr" ? "Prénom" : "First name"}</Label>
                <Input data-testid="staff-first" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div>
                <Label>{lang === "fr" ? "Nom" : "Last name"}</Label>
                <Input data-testid="staff-last" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="staff-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <Label>{lang === "fr" ? "Rôle" : "Role"}</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{lang === "fr" ? r.label_fr : r.label_en}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">
                {roleMeta(form.role)[lang === "fr" ? "desc_fr" : "desc_en"]}
              </div>
            </div>
            {!editing && (
              <div>
                <Label>{lang === "fr" ? "Mot de passe (≥ 8 caractères)" : "Password (≥ 8 chars)"}</Label>
                <PasswordInput data-testid="staff-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
            )}
            <Button onClick={save} className="w-full" data-testid="staff-save">
              {lang === "fr" ? "Enregistrer" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwOpen} onOpenChange={(v) => !v && setPwOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lang === "fr" ? `Réinitialiser le mot de passe de ${pwOpen?.email}` : `Reset password for ${pwOpen?.email}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === "fr" ? "Nouveau mot de passe (≥ 8 caractères)" : "New password (≥ 8 chars)"}</Label>
              <PasswordInput data-testid="reset-pw-input" value={newPw} onChange={e => setNewPw(e.target.value)} autoFocus />
            </div>
            <Button onClick={resetPw} className="w-full" data-testid="reset-pw-confirm">
              {lang === "fr" ? "Réinitialiser" : "Reset"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
